import { Input, Select } from '@/components/ui/FormControls';
import { useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import Link from '@/components/common/AccessLink';
import {
  Search,
  ChevronDown,
  ChevronRight,
  Layers,
  Trash2,
  ExternalLink,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useConfirm } from '@/contexts/ConfirmContext';
import { useCashbook, CashbookFilters } from '@/hooks/useAccounts';
import { useCashAccounts, useVoidCashTransaction } from '@/hooks/useCash';
import LoadingState from '@/components/common/LoadingState';
import ErrorState from '@/components/common/ErrorState';
import EmptyState from '@/components/common/EmptyState';
import ExportActions from '@/components/common/ExportActions';
import {
  CashbookRow,
  CashbookTypeKey,
  ALL_TYPE_KEYS,
  TYPE_KEY_LABELS,
  groupSalesByDay,
  formatINR,
  formatDisplayDate,
  sourceLabel,
  parseRpcError,
} from '@/utils/cashbookCalc';
import {
  buildCashbookExcelOptions,
  buildCashbookPdfOptions,
} from '@/utils/cashbookExport';
import type { AccountsContextValue } from '../AccountsLayout';

interface CashbookTabProps {
  from?: string | null;
  to?: string | null;
  onOpenRecordExpense?: () => void;
}

export default function CashbookTab(props: CashbookTabProps) {
  const ctx = useOutletContext<AccountsContextValue | undefined>();
  const from = props.from !== undefined ? props.from : ctx?.from ?? null;
  const to = props.to !== undefined ? props.to : ctx?.to ?? null;
  const onOpenRecordExpense = props.onOpenRecordExpense ?? ctx?.openExpenseModal;
  const { role, organization } = useAuth();
  const confirm = useConfirm();
  const voidMutation = useVoidCashTransaction();
  const { data: accounts } = useCashAccounts();

  // Filters state
  const [selectedAccountId, setSelectedAccountId] = useState<string>('');
  const [selectedType, setSelectedType] = useState<CashbookTypeKey | 'all'>('all');
  const [search, setSearch] = useState('');
  const [groupByDay, setGroupByDay] = useState(false);
  const [expandedDates, setExpandedDates] = useState<Set<string>>(new Set());
  const [page, setPage] = useState(1);
  const pageSize = 100;

  const filters: CashbookFilters = {
    from,
    to,
    account_id: selectedAccountId || null,
    types: selectedType !== 'all' ? [selectedType] : undefined,
    search: search.trim() || null,
    page,
    page_size: pageSize,
  };

  const { data: cashbook, isLoading, error, refetch } = useCashbook(filters);

  const isOwnerOrAdmin = role === 'owner' || role === 'admin';

  const toggleDateExpand = (date: string) => {
    setExpandedDates((prev) => {
      const next = new Set(prev);
      if (next.has(date)) next.delete(date);
      else next.add(date);
      return next;
    });
  };

  const handleVoidRow = async (row: CashbookRow) => {
    if (!row.voidable) return;
    const isOk = await confirm({
      title: 'Void Cash Transaction',
      description: `Are you sure you want to void this ${row.transaction_type.replace(/_/g, ' ')} of ₹${Math.abs(row.amount)}? This will reverse the account balance.`,
      variant: 'destructive',
      confirmText: 'Void Transaction',
    });
    if (!isOk) return;

    try {
      await voidMutation.mutateAsync({
        transaction_id: row.id,
        reason: 'Voided from Cashbook',
      });
      toast.success('Transaction voided successfully');
      refetch();
    } catch (err) {
      const parsed = parseRpcError(err);
      toast.error(parsed.message);
    }
  };

  const rows = cashbook?.rows ?? [];
  const displayRows = groupByDay ? groupSalesByDay(rows) : null;

  const selectedAccountName =
    accounts?.find((a) => a.id === selectedAccountId)?.name ?? undefined;

  return (
    <div className="space-y-4">
      {/* Top action and filter bar */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        {/* Type & Account pills */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Account selector */}
          <Select
            value={selectedAccountId}
            onChange={(e) => {
              setSelectedAccountId(e.target.value);
              setPage(1);
            }}
            className="h-8 rounded-lg border border-border bg-card px-2.5 text-xs text-foreground shadow-xs focus:border-primary focus:outline-none"
            aria-label="Filter by account"
          >
            <option value="">All Accounts</option>
            {accounts?.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </Select>

          {/* Type filters */}
          <div className="obsidian-panel inline-flex rounded-lg border border-border bg-card/60 p-0.5 shadow-xs">
            <button
              type="button"
              onClick={() => {
                setSelectedType('all');
                setPage(1);
              }}
              className={`rounded-md px-2.5 py-1 text-xs font-medium transition-all ${
                selectedType === 'all'
                  ? 'bg-primary text-primary-foreground shadow-xs'
                  : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground'
              }`}
            >
              All Types
            </button>
            {ALL_TYPE_KEYS.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => {
                  setSelectedType(t);
                  setPage(1);
                }}
                className={`rounded-md px-2.5 py-1 text-xs font-medium transition-all ${
                  selectedType === t
                    ? 'bg-primary text-primary-foreground shadow-xs'
                    : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground'
                }`}
              >
                {TYPE_KEY_LABELS[t]}
              </button>
            ))}
          </div>
        </div>

        {/* Search, Grouping toggle, and Exports */}
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search particular, party..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              className="h-8 w-44 rounded-lg border border-border bg-card pl-8 pr-2.5 text-xs text-foreground placeholder:text-muted-foreground shadow-xs focus:border-primary focus:outline-none sm:w-56"
              helpLabel="Search records"
            />
          </div>

          <button
            type="button"
            onClick={() => setGroupByDay(!groupByDay)}
            className={`inline-flex h-8 items-center gap-1.5 rounded-lg border border-border px-2.5 text-xs font-medium transition-all ${
              groupByDay
                ? 'bg-primary/10 text-primary border-primary/30'
                : 'bg-card text-muted-foreground hover:text-foreground'
            }`}
            title="Group multiple daily sales into single folded rows"
          >
            <Layers className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Group Sales</span>
          </button>

          {/* Export Actions */}
          <ExportActions
            excelOptions={() =>
              buildCashbookExcelOptions({
                rows,
                totals: cashbook?.totals,
                opening_balance: cashbook?.opening_balance,
                from,
                to,
                orgName: organization?.name,
                accountName: selectedAccountName,
              })
            }
            pdfOptions={() =>
              buildCashbookPdfOptions({
                rows,
                totals: cashbook?.totals,
                opening_balance: cashbook?.opening_balance,
                from,
                to,
                orgName: organization?.name,
                accountName: selectedAccountName,
              })
            }
          />
        </div>
      </div>

      {/* Main Table / Content */}
      {isLoading ? (
        <LoadingState message="Loading cashbook ledger…" />
      ) : error ? (
        <ErrorState message={error.message} onRetry={refetch} />
      ) : rows.length === 0 ? (
        <EmptyState
          title="No transactions found"
          description="No cashbook entries match the selected date range and filters."
          action={
            isOwnerOrAdmin ? (
              <div className="flex gap-2">
                <Link
                  to="/accounts/cashbook/import"
                  className="rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted"
                >
                  Import From Excel
                </Link>
                {onOpenRecordExpense && (
                  <button
                    type="button"
                    onClick={() => onOpenRecordExpense()}
                    className="rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90"
                  >
                    + Record Expense
                  </button>
                )}
              </div>
            ) : undefined
          }
        />
      ) : (
        <div className="obsidian-surface overflow-hidden rounded-xl border border-border bg-card shadow-xs">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="border-b border-border bg-muted/40 font-medium text-muted-foreground">
                <tr>
                  <th className="py-2.5 px-3">Date</th>
                  <th className="py-2.5 px-3">Particular / Description</th>
                  <th className="py-2.5 px-3">Type</th>
                  <th className="py-2.5 px-3">Category / Party</th>
                  <th className="py-2.5 px-3">Account</th>
                  <th className="py-2.5 px-3 text-right">Income (₹)</th>
                  <th className="py-2.5 px-3 text-right">Expense (₹)</th>
                  <th className="py-2.5 px-3 text-right">A/C Balance (₹)</th>
                  {isOwnerOrAdmin && <th className="py-2.5 px-3 text-center w-10">Actions</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-border/40">
                {/* When grouping by day is active */}
                {groupByDay && displayRows ? (
                  displayRows.map((item) => {
                    if (item.kind === 'sale_group') {
                      const isExpanded = expandedDates.has(item.date);
                      return (
                        <tr key={item.key} className="bg-primary/5 hover:bg-primary/10 transition-colors">
                          <td className="py-2.5 px-3 font-medium text-foreground">
                            <button
                              type="button"
                              onClick={() => toggleDateExpand(item.date)}
                              className="inline-flex items-center gap-1.5 text-primary hover:underline font-mono"
                            >
                              {isExpanded ? (
                                <ChevronDown className="h-3.5 w-3.5" />
                              ) : (
                                <ChevronRight className="h-3.5 w-3.5" />
                              )}
                              {formatDisplayDate(item.date)}
                            </button>
                          </td>
                          <td className="py-2.5 px-3 text-foreground font-medium" colSpan={4}>
                            <div className="flex items-center gap-2">
                              <span>Daily Sales Summary</span>
                              <span className="rounded-full bg-teal-500/10 px-2 py-0.5 text-[10px] text-teal-600 dark:text-teal-400 font-normal">
                                {item.rows.length} sales grouped
                              </span>
                            </div>
                          </td>
                          <td className="py-2.5 px-3 text-right font-semibold text-emerald-600 dark:text-emerald-400">
                            +{formatINR(item.income)}
                          </td>
                          <td className="py-2.5 px-3 text-right text-muted-foreground">—</td>
                          <td className="py-2.5 px-3 text-right font-medium text-foreground">
                            {formatINR(item.running_balance)}
                          </td>
                          {isOwnerOrAdmin && <td></td>}
                        </tr>
                      );
                    }
                    const r = item.row;
                    return renderRow(r, isOwnerOrAdmin, handleVoidRow);
                  })
                ) : (
                  rows.map((r) => renderRow(r, isOwnerOrAdmin, handleVoidRow))
                )}
              </tbody>

              {/* Totals & Opening/Closing Footer */}
              <tfoot className="border-t-2 border-border bg-muted/60 font-semibold text-foreground">
                {/* Opening balance row if unfiltered */}
                {!cashbook?.is_filtered && (
                  <tr className="border-b border-border/40 text-[11px] text-muted-foreground">
                    <td colSpan={5} className="py-2 px-3">
                      Opening Cash Balance
                    </td>
                    <td colSpan={2} className="py-2 px-3 text-right">
                      Prior to period
                    </td>
                    <td className="py-2 px-3 text-right font-mono text-foreground">
                      {formatINR(cashbook?.opening_balance)}
                    </td>
                    {isOwnerOrAdmin && <td></td>}
                  </tr>
                )}
                <tr>
                  <td colSpan={5} className="py-3 px-3">
                    Totals for {cashbook?.total_rows ?? rows.length} records
                  </td>
                  <td className="py-3 px-3 text-right text-emerald-600 dark:text-emerald-400 font-mono">
                    +{formatINR(cashbook?.totals.income)}
                  </td>
                  <td className="py-3 px-3 text-right text-rose-600 dark:text-rose-400 font-mono">
                    -{formatINR(cashbook?.totals.expense)}
                  </td>
                  <td className="py-3 px-3 text-right font-mono font-bold">
                    {formatINR(cashbook?.totals.closing_balance)}
                  </td>
                  {isOwnerOrAdmin && <td></td>}
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Pagination */}
          {cashbook && cashbook.total_rows > pageSize && (
            <div className="flex items-center justify-between border-t border-border px-4 py-3 text-xs text-muted-foreground">
              <div>
                Showing {(page - 1) * pageSize + 1} to{' '}
                {Math.min(page * pageSize, cashbook.total_rows)} of {cashbook.total_rows} entries
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="rounded border border-border px-2.5 py-1 text-xs hover:bg-muted disabled:opacity-40"
                >
                  Previous
                </button>
                <span className="text-foreground">Page {page}</span>
                <button
                  type="button"
                  disabled={page * pageSize >= cashbook.total_rows}
                  onClick={() => setPage((p) => p + 1)}
                  className="rounded border border-border px-2.5 py-1 text-xs hover:bg-muted disabled:opacity-40"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Row Renderer ─────────────────────────────────────────────
function renderRow(
  r: CashbookRow,
  isOwnerOrAdmin: boolean,
  handleVoidRow: (r: CashbookRow) => void
) {
  const isIncome = r.direction === 'income';
  const isExpense = r.direction === 'expense';

  const typeColorMap: Record<string, string> = {
    sale_payment: 'bg-teal-500/10 text-teal-600 dark:text-teal-400 border-teal-500/20',
    purchase_payment: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20',
    capital_contribution: 'bg-primary/10 text-primary border-primary/20',
    expense: 'bg-rose-500/10 text-rose-600 dark:text-rose-400 border-rose-500/20',
    refund: 'bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-500/20',
    adjustment: 'bg-slate-500/10 text-slate-600 dark:text-slate-400 border-slate-500/20',
    opening_balance: 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20',
    transfer_in: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20',
    transfer_out: 'bg-orange-500/10 text-orange-600 dark:text-orange-400 border-orange-500/20',
  };

  const badgeClass =
    typeColorMap[r.transaction_type] ??
    'bg-muted text-muted-foreground border-border';

  return (
    <tr key={r.id} className="hover:bg-muted/30 transition-colors">
      <td className="py-2.5 px-3 font-mono text-muted-foreground whitespace-nowrap">
        {formatDisplayDate(r.transaction_date)}
      </td>
      <td className="py-2.5 px-3 font-medium text-foreground">
        <div className="flex items-center gap-1.5">
          <span>{r.particular}</span>
          {r.sale_number && (
            <Link
              to={`/sales/${r.reference_id}`}
              className="inline-flex items-center text-[10px] text-primary hover:underline"
              title="View Sale"
            >
              #{r.sale_number}
              <ExternalLink className="ml-0.5 h-2.5 w-2.5" />
            </Link>
          )}
          {r.purchase_number && (
            <Link
              to={`/purchases/${r.reference_id}`}
              className="inline-flex items-center text-[10px] text-primary hover:underline"
              title="View Purchase"
            >
              #{r.purchase_number}
              <ExternalLink className="ml-0.5 h-2.5 w-2.5" />
            </Link>
          )}
        </div>
      </td>
      <td className="py-2.5 px-3 whitespace-nowrap">
        <span
          className={`inline-block rounded-md border px-2 py-0.5 text-[10px] font-medium capitalize ${badgeClass}`}
        >
          {r.transaction_type.replace(/_/g, ' ')}
        </span>
      </td>
      <td className="py-2.5 px-3 text-muted-foreground">
        {sourceLabel(r)}
      </td>
      <td className="py-2.5 px-3 text-muted-foreground whitespace-nowrap">
        {r.account_name ?? '—'}
      </td>
      <td className="py-2.5 px-3 text-right font-mono font-medium text-emerald-600 dark:text-emerald-400">
        {isIncome && r.income_amount > 0 ? `+${formatINR(r.income_amount)}` : '—'}
      </td>
      <td className="py-2.5 px-3 text-right font-mono font-medium text-rose-600 dark:text-rose-400">
        {isExpense && r.expense_amount > 0 ? `-${formatINR(r.expense_amount)}` : '—'}
      </td>
      <td className="py-2.5 px-3 text-right font-mono font-medium text-foreground">
        {formatINR(r.running_balance)}
      </td>
      {isOwnerOrAdmin && (
        <td className="py-2.5 px-3 text-center">
          {r.voidable ? (
            <button
              type="button"
              onClick={() => handleVoidRow(r)}
              className="rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
              title="Void transaction"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          ) : (
            <span className="text-muted-foreground/30 text-[10px]">—</span>
          )}
        </td>
      )}
    </tr>
  );
}
